import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { Course } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { BookOpen, Plus, Edit, Users } from 'lucide-react'

export function CoursesListPage() {
  useDocumentTitle('講座管理')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentTenant, memberships } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [courses, setCourses] = useState<Course[]>([])

  useEffect(() => {
    const isTenantAdmin = memberships.some(
      (m) => m.tenant_id === currentTenant?.tenant_id && m.role_in_tenant === 'tenant_admin'
    )

    if (!isTenantAdmin && memberships.length > 0) {
      navigate('/mypage', { replace: true })
      return
    }

    if (user && currentTenant) {
      fetchCourses()
    }
  }, [user, currentTenant])

  const fetchCourses = async () => {
    if (!currentTenant) return

    try {
      setLoading(true)
      setError(null)

      const { data, error: coursesError } = await supabase
        .from('courses')
        .select('*')
        .eq('tenant_id', currentTenant.tenant_id)
        .order('created_at', { ascending: false })

      if (coursesError) throw coursesError

      setCourses(data || [])
    } catch (err: any) {
      console.error('Error fetching courses:', err)
      setError(err.message || '講座の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="講座を読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">講座管理</h1>
            <p className="mt-2 text-gray-600">講座の作成・編集・管理</p>
          </div>
          <button
            onClick={() => navigate('/tenant/courses/new')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
          >
            <Plus className="h-5 w-5" />
            新規講座作成
          </button>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={fetchCourses} />
          </div>
        )}

        {courses.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">講座がありません</h3>
            <p className="text-gray-600 mb-6">新しい講座を作成して始めましょう</p>
            <button
              onClick={() => navigate('/tenant/courses/new')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
            >
              <Plus className="h-5 w-5" />
              新規講座作成
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div key={course.course_id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex-1">
                    {course.title}
                  </h3>
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      course.is_published
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {course.is_published ? '公開中' : '下書き'}
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {course.description || '説明なし'}
                </p>

                <div className="space-y-2 mb-4">
                  {course.ambassador_name && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>講師: {course.ambassador_name}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/tenant/courses/${course.course_id}/edit`)}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm font-medium"
                    >
                      <Edit className="h-4 w-4" />
                      編集
                    </button>
                    <button
                      onClick={() => navigate(`/tenant/courses/${course.course_id}/lessons`)}
                      className="flex-1 px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition text-sm"
                    >
                      教材管理
                    </button>
                  </div>
                  <button
                    onClick={async () => {
                      // 講座に紐づく試験を取得
                      const { data: examData } = await supabase
                        .from('exams')
                        .select('exam_id')
                        .eq('course_id', course.course_id)
                        .single()

                      if (examData) {
                        navigate(`/tenant/exams/${examData.exam_id}/questions`)
                      }
                    }}
                    className="w-full px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition text-sm"
                  >
                    試験問題管理
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  )
}
